import { createSlice } from "@reduxjs/toolkit";
import { setLoading } from "../auth/authSliceNew";
import { fynitoApi } from "../../../Components/Services/axios";
import toastify from "../../../helper/toastify";
import apiCache from "../../../services/apiCache";

const initialState = {
  addressList: [],
  loading1: false,
  error: null,
  currentAddress: null,
  isEditing: false,
  selectedAddress: null,
  addressMessage: null,
};

const addressSlice = createSlice({
  name: "address",
  initialState,
  reducers: {
    setLoading1: (state, action) => {
      state.loading1 = action.payload;
    },
    setAddressList: (state, action) => {
      state.addressList = action.payload;
    },
    setCurrentAddress: (state, action) => {
      state.currentAddress = action.payload;
    },
    setIsEditing: (state, action) => {
      state.isEditing = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    setSelectedAddress: (state, action) => {
      state.selectedAddress = action.payload;
      // Optionally save to localStorage for persistence across page refreshes
      if (action.payload) {
        localStorage.setItem("selectedAddress", JSON.stringify(action.payload));
      }
    },
    setAddressMessage: (state, action) => {
      state.addressMessage = action.payload;
    },
  },
});

export const {
  setError,
  setLoading1,
  setAddressList,
  setCurrentAddress,
  setIsEditing,
  setSelectedAddress,
    setAddressMessage,
} = addressSlice.actions;

export const fetchAddress = () => async (dispatch) => {
  // Only fetch addresses if user is authenticated
  const token = localStorage.getItem('token');
  if (!token) {
    return; // Silently return if no token
  }
  
  try {
    // Check cache first
    const cacheKey = apiCache.getCacheKey("/address-list", "GET");
    const cachedData = apiCache.get(cacheKey, 120000); // 2 minutes cache
    
    if (cachedData) {
      dispatch(setAddressList(cachedData));
      return;
    }
    
    // Check if request is already pending
    if (apiCache.isPending(cacheKey)) {
      const pendingResponse = await apiCache.getPending(cacheKey);
      if (pendingResponse?.data?.status_code === 200) {
        dispatch(setAddressList(pendingResponse.data.data));
        apiCache.set(cacheKey, pendingResponse.data.data);
      }
      return;
    }
    
    // Make API call with pending request tracking
    const requestPromise = fynitoApi.get("/address-list");
    apiCache.setPending(cacheKey, requestPromise);
    
    const res = await requestPromise;
    
    if (res.data.status_code === 200) {
      dispatch(setAddressList(res.data.data));
      apiCache.set(cacheKey, res.data.data);
    } else {
      toastify.error(res.data.message);
    }
  } catch (error) {
    // Check if this is a 401 error that's already been handled by the interceptor
    const is401Error = error.response?.status === 401 || 
                      error.status === 401 ||
                      error._isHandled401 ||
                      error.isHandled;
    
    // If it's a handled 401, silently return (interceptor already handled logout/redirect)
    if (!is401Error) {
      // Only show error for non-401 errors
      const errorMessage = error.response?.data?.message || error.message;
      if (errorMessage && !errorMessage.includes('401')) {
        console.error("Error fetching addresses:", errorMessage);
      }
    }
    // Silently handle 401 errors as they're already handled by the interceptor
  }
};

export const saveAddress = (payload) => async (dispatch) => {
  try {
    const res = await fynitoApi.post("/address-store", payload);
    if (res.data.status_code === 200) {
      toastify.success("Address Stored Successfully");
      dispatch(fetchAddress());
    } else {
      toastify.error(res.data.message);
    }
  } catch (error) {
  }
};

export const updateAddress = (payload) => async (dispatch) => {
  try {
    const res = await fynitoApi.post(`/address-update`, payload);
    if (res.data.status_code === 200) {
      toastify.success(res.data.message);
      dispatch(fetchAddress());
      dispatch(setIsEditing(false));
      dispatch(setCurrentAddress(null));
      return { success: true };
    } else {
      toastify.error(res.data.message);
      return { success: false };
    }
  } catch (error) {
    return { success: false };
  }
};

export const deleteAddress = (payload) => async (dispatch) => {
  try {
    const res = await fynitoApi.post(`/address-delete`, payload);
    if (res.data.status_code === 200) {
      toastify.success(res.data.message);
      dispatch(fetchAddress());
    } else {
      toastify.error(res.data.message);
    }
  } catch (error) {
  }
};
export const initializeSelectedAddress = () => (dispatch) => {
  const savedAddress = localStorage.getItem("selectedAddress");
  if (savedAddress) {
    dispatch(setSelectedAddress(JSON.parse(savedAddress)));
  }
};

export default addressSlice.reducer;
